/**
 * Texture resolution: a `Surface` from the project XML → a URL the browser can load.
 *
 * Two very different sources:
 *  - catalog textures live INSIDE the Unity app (`Assets/Resources/Textures/...`) and have
 *    to be published; in dev they are served straight from the Unity working copy.
 *  - `[custom]` user uploads are already public on files.icandesignapp.com, but only under
 *    the `.texture.<ext>` name — the raw `<name>.<ext>` is a 404.
 */
import nativeNormals from './nativeNormals.json';
import type { Surface } from '../core/hp/types';
import { surfaceRepeat } from '../core/hp/surfaceTransform';
import { knownBuiltinSurface } from '../state/builtinTextures';

export const FILES_ORIGIN = 'https://files.icandesignapp.com';

/** Where the app's unpublished textures live on the owner's machine; none on the site, which serves no such path. */
export let CATALOG_TEXTURE_BASE: string | null = '/hp/tex';

export function setCatalogTextureBase(base: string | null): void {
  CATALOG_TEXTURE_BASE = base ? base.replace(/\/$/, '') : null;
}

export interface ResolvedTexture {
  url: string;
  /** Texture repeats per metre, derived from the tile size. */
  repeat: [number, number];
}

/** Where the pictures a project names by their path in the app's model tree are staged on the owner's machine; none on the site. */
export let MODEL_ASSET_BASE: string | null = '/hp/model';

export function setModelAssetBase(base: string | null): void {
  MODEL_ASSET_BASE = base ? base.replace(/\/$/, '') : null;
}

/**
 * Where the old static library kept each model's unpacked archive pictures (`public/hp/models`,
 * cloud models under `cloud/`), for the tools that still stage it. The editor sets none: a
 * format-2 web GLB carries those pictures (`ModelLibrary.archivePicture`), and no server serves
 * them on their own, so a path here would only ask for a 404.
 */
export let MODEL_OWN_TEXTURE_BASE: string | null = null;

export function setModelOwnTextureBase(base: string | null): void {
  MODEL_OWN_TEXTURE_BASE = base ? base.replace(/\/$/, '') : null;
}

/** The raw `<name>.<ext>` is a 404; only the `.texture.<ext>` form is served. */
export function customTextureUrl(name: string): string {
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '.jpg';
  return `${FILES_ORIGIN}/${base}.texture${ext}`;
}

export function resolveTexture(s: Surface | undefined, explicitTiling = false): ResolvedTexture | null {
  if (!s) return null;
  const repeat = repeatOf(s, explicitTiling);

  switch (s.textureKind) {
    case 'custom':
      return s.customTexture ? { url: customTextureUrl(s.customTexture), repeat } : null;
    case 'catalog': {
      if (!s.texture) return null;
      // The catalog's picture for the app's texture; else the path under the base, which only the owner's machine
      // has — on the site, which has no base, nothing, so the slot keeps the model's look or its colour.
      const published = knownBuiltinSurface(s.texture);
      if (published?.customTexture) return { url: customTextureUrl(published.customTexture), repeat };
      return CATALOG_TEXTURE_BASE ? { url: `${CATALOG_TEXTURE_BASE}/${s.texture}`, repeat } : null;
    }
    case 'model':
      return s.texture && MODEL_ASSET_BASE ? { url: `${MODEL_ASSET_BASE}/${s.texture}`, repeat } : null;
    case 'modelOwn': {
      if (!s.texture || !s.textureOwner || !MODEL_OWN_TEXTURE_BASE) return null;
      // Local ids end in the model filename (category/id/id), whereas the image is
      // staged beside that GLB (category/id/image), not under a cloud archive.
      const slash = s.textureOwner.lastIndexOf('/');
      if (slash >= 0) return { url: `${MODEL_OWN_TEXTURE_BASE}/${s.textureOwner.slice(0, slash)}/${s.texture}`, repeat };
      return { url: `${MODEL_OWN_TEXTURE_BASE}/cloud/${s.textureOwner}/${s.texture}`, repeat };
    }
    default:
      return null;
  }
}

/** The specular colour three.js multiplies its 0.04 base reflectance by, for an sRGB `_SpecColor` grey. */
const specularOfGrey = (grey: number) => ((grey + .055) / 1.055) ** 2.4 / .04;

/**
 * The app's `Resources/Shaders/<type>.mat` assets: roughness = 1 − `_Glossiness`, metalness = `_Metallic`, and for the
 * assets on `StandardSpecular` their `_SpecColor`. `Type` in the XML is the coarser 3ds Max export class;
 * `MaterialName` is the one that picks the asset.
 */
const MTL: Record<string, { roughness: number; metalness: number; emissive?: number; specular?: number }> = {
  mtl_plastic: { roughness: 0.40, metalness: 0 },
  mtl_polished: { roughness: 0.03, metalness: 0, specular: specularOfGrey(.137) },
  mtl_glass: { roughness: 0.03, metalness: 0, specular: specularOfGrey(.137) },
  mtl_glass_transparent: { roughness: 0.03, metalness: 0 },
  mtl_mirror: { roughness: 0.01, metalness: 0.99 },
  mtl_metal_matte: { roughness: 0.30, metalness: 0.7 },
  mtl_metal_gloss: { roughness: 0.10, metalness: 0.8 },
  mtl_steel: { roughness: 0.40, metalness: 0.8 },
  'mtl_metal-cast-iron': { roughness: 0.30, metalness: 1.0 },
  mtl_wood: { roughness: 0.40, metalness: 0 },
  mtl_fabric: { roughness: 0.60, metalness: 0 },
  mtl_fabric_transparent: { roughness: 0.60, metalness: 0 },
  mtl_mesh: { roughness: 1.00, metalness: 0 },
  mtl_mesh_colored: { roughness: 1.00, metalness: 0 },
  mtl_ceramic: { roughness: 0.20, metalness: 0, specular: specularOfGrey(.235) },
  mtl_rubber: { roughness: 0.473, metalness: 0, specular: specularOfGrey(.183) },
  mtl_paint: { roughness: 0.20, metalness: 0 },
  mtl_paper: { roughness: 0.60, metalness: 0 },
  mtl_light: { roughness: 0.70, metalness: 0, emissive: 0.254 },
  mtl_Bodbyn_gray: { roughness: 0.50, metalness: 0, specular: specularOfGrey(.2196) },
  mtl_Walls_White: { roughness: 0.50, metalness: 0 },
};

function preset(s: Surface | undefined) {
  if (!s?.type) return undefined;
  // Unity collapses every mtl_steel* variant onto the one shader.
  const key = s.type.startsWith('mtl_steel') ? 'mtl_steel' : s.type;
  return MTL[key];
}

/** As `MaterialUtils.CreateMaterial`: an app type's asset first, `Shininess` only without one and above zero; else
 *  the app's `Default-Standard.mat` (`_Glossiness` .12). A model's override passes its own roughness as `fallback`
 *  instead, where the app takes Default-Standard too. */
export function roughnessOf(s: Surface | undefined, fallback = 0.88): number {
  const row = preset(s);
  if (row) return row.roughness;
  const shininess = s?.shininess ?? 0;
  return shininess > 0 ? Math.max(1 - shininess, 0.03) : fallback;
}

export function specularOf(s: Surface | undefined): number {
  return preset(s)?.specular ?? 1;
}

/** A wall face of a plain colour without a type is drawn at `_Glossiness` .4 (`WallSideController.SetMaterial`); a face
 *  with no finish is the app's `#nofinish` marker, which has a type. */
export function wallRoughnessOf(s: Surface | undefined): number {
  return s && !s.type && !s.texture && !s.customTexture ? .6 : roughnessOf(s);
}

export function metalnessOf(s: Surface | undefined): number {
  return preset(s)?.metalness ?? 0;
}

export function emissiveOf(s: Surface | undefined): number {
  return preset(s)?.emissive ?? 0;
}

/**
 * Effective opacity. Preserve the authored glass alpha, as Lite MaterialLook does.
 */
export function opacityOf(s: Surface | undefined): number | undefined {
  if (!s) return undefined;
  const transparent = s.type === 'mtl_glass' || s.type === 'mtl_fabric_transparent';
  if (s.opacity !== undefined && s.opacity < 1) {
    return s.opacity;
  }
  return transparent ? 0.35 : undefined;
}

/**
 * A room's floor, ceiling or wall face as the app draws it. Its colour's alpha makes it see-through only where it has no
 * picture: the app turns its shader «Transparent/» only without a TexturePath (`MaterialData.CalculatedShader`), so the
 * parquet of the app's ready rooms, written with a = 0, is solid there. Glass stays glass.
 */
export function roomSurfaceOpacity(s: Surface | undefined): number | undefined {
  const glass = s?.type === 'mtl_glass' || s?.type === 'mtl_fabric_transparent';
  return (s?.texture || s?.customTexture) && !glass ? undefined : opacityOf(s);
}

/**
 * Texture repeat for a surface. Primitives, openings, plinths and moldings have UVs
 * spanning 0..1 and use the material's explicit `<Tiling>`; everything else derives the
 * repeat from the texture's real-world tile size.
 */
export function repeatOf(s: Surface | undefined, explicitTiling: boolean): [number, number] {
  return surfaceRepeat(s, explicitTiling);
}

/** Native diffuse-file hash or globally unique filename matches generated by stage-native-render-assets.py. */
export function normalTextureOf(surface: Surface | undefined): string | undefined {
  return surface?.textureKind === 'catalog' && surface.texture
    ? (nativeNormals as Record<string, string>)[surface.texture] : undefined;
}
